import type { SaturatedOilViscosityParameters } from "../../d";

export function saturatedOilViscosityCalculator(
  params: SaturatedOilViscosityParameters
): {
  saturatedOilViscosity: number;
  normalizedGasSg: number;
  F: number;
  y: number;
  F_22_31: number;
  F_10_22: number;
  F_lt_10: number;
  saturatedOilViscosity_gt_31: number;
  saturatedOilViscosity_22_31: number;
  saturatedOilViscosity_10_22: number;
  saturatedOilViscosity_lt_10: number;
} {
  const {
    solutionGasRso,
    oilAPI,
    surfaceTemperatureF,
    tubingPressurePSIA,
    gasSpecificGravity
  } = params;
  const { deadOilViscosity } = params;

  // Q60: =10**(-0.00081*J12)
  const y = 10 ** (-0.00081 * solutionGasRso);

  // Q62: =IFERROR(D8*(1+(5.912*10**(-5))*D7*D6*LOG(D5/114.7)),D8)
  let normalizedGasSg =
    gasSpecificGravity *
    (1 +
      (5.912 * 10) ** -5 *
        oilAPI *
        surfaceTemperatureF *
        Math.log(tubingPressurePSIA / 114.7));
  normalizedGasSg = isNaN(normalizedGasSg)
    ? gasSpecificGravity
    : normalizedGasSg;

  // Q64 =(25.1921*(J12+100)**-0.6487)*Q17**(2.7516*(J12+150)**-0.2135)
  const saturatedOilViscosity_gt_31 =
    25.1921 *
    (solutionGasRso + 100) ** -0.6487 *
    deadOilViscosity ** (2.7516 * (solutionGasRso + 150) ** -0.2135);

  // Q66: =(0.2038+0.8591*10**(-0.000845*J12))*Q17**(0.3855+0.5664*Q60)
  const F_22_31 =
    (0.2038 + 0.8591 * 10 ** (-0.000845 * solutionGasRso)) *
    deadOilViscosity ** (0.3855 + 0.5664 * y);
  // Q68: =0.0132+0.9821*Q66-0.005215*Q66**2
  const saturatedOilViscosity_22_31 =
    0.0132 + 0.9821 * F_22_31 - 0.005215 * F_22_31 ** 2;

  // Q70: =(0.2478+0.6114*10**(-0.000845*J12))*Q17**(0.4731+0.5158*Q60)
  const F_10_22 =
    (0.2478 + 0.6114 * 10 ** (-0.000845 * solutionGasRso)) *
    deadOilViscosity ** (0.4731 + 0.5158 * y);
  // Q72: =-0.6311+1.078*Q70-0.003653*Q70**2
  const saturatedOilViscosity_10_22 =
    -0.6311 + 1.078 * F_10_22 - 0.003653 * F_10_22 ** 2;

  // Q74: =(-0.0335+1.0785*10**(-0.000845*J12))*Q17**(0.5798+0.3432*Q60)
  const F_lt_10 =
    (-0.0335 + 1.0785 * 10 ** (-0.000845 * solutionGasRso)) *
    deadOilViscosity ** (0.5798 + 0.3432 * y);
  // Q76: =2.3945+0.8927*Q74+0.001567*Q74**2
  const saturatedOilViscosity_lt_10 =
    2.3945 + 0.8927 * F_lt_10 + 0.001567 * F_lt_10 ** 2;

  let F = 0;
  let saturatedOilViscosity = 0;
  if (oilAPI > 31.1) {
    saturatedOilViscosity = saturatedOilViscosity_gt_31;
  } else if (oilAPI > 22.3 && oilAPI <= 31.1) {
    F = F_22_31;
    saturatedOilViscosity = saturatedOilViscosity_22_31;
  } else if (oilAPI > 10 && oilAPI <= 22.3) {
    F = F_10_22;
    saturatedOilViscosity = saturatedOilViscosity_10_22;
  } else if (oilAPI <= 10) {
    F = F_lt_10;
    saturatedOilViscosity = saturatedOilViscosity_lt_10;
  }

  return {
    normalizedGasSg,
    saturatedOilViscosity,
    F,
    F_22_31,
    F_10_22,
    F_lt_10,
    y,
    saturatedOilViscosity_gt_31,
    saturatedOilViscosity_22_31,
    saturatedOilViscosity_10_22,
    saturatedOilViscosity_lt_10
  };
}
